import React, { Fragment, useEffect, useState, useRef } from "react";
import { connect, useDispatch } from "react-redux";
import * as numberUtils from "util/number";
import { bool, shape, arrayOf, number, oneOfType, string } from "prop-types";
import { actions as loansActions, selectors as loansSelectors } from "reducers/loans";
import * as Yup from "yup";
import * as i18n from "util/i18n";
import useSchema from "hooks/useSchema";
import MainContainer from "pages/_components/MainContainer";
import CustomButton from "pages/_components/CustomButton/CustomButton";
import { Col, Row } from "react-bootstrap";
import { Field, Form, Formik } from "formik";
import AmountField from "pages/_components/fields/formik/AmountField";
import Selector from "pages/_components/fields/formik/Selector";
import CustomProductHead from "pages/_components/CustomProductHead";
import ViewTitle from "pages/_components/header/components/ViewTitle";
import CustomData from "pages/_components/CustomData/CustomData";
import GridTable from "pages/_components/GridTable/GridTable";
import Button from "pages/_components/Button";
import ProductSelector from "pages/_components/fields/formik/FormikProductSelector";
import MasInformacion from "pages/loans/_components/MasInformacion";
import DateField from "pages/_components/fields/DateField";
import Date from "pages/_components/fields/DateField";
import * as configUtils from "util/config";
import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import Drawer from "pages/_components/Drawer/Drawer";
import UnsecuredLoanFormConfirm from "./UnsecuredLoanFormConfirm";
import Notification from "pages/_components/Notification";

const UnsecuredLoanForm = ({
    fetching,
    fetchingSimulation,
    qualificationAvailable,
    qualificationLimit,
    minAmount,
    maxAmount,
    isDesktop,
    accounts,
    loanType,
    rate,
    preferentialRates,
    loanSimulation,
}) => {
    const formRef = useRef(null);
    const dispatch = useDispatch();
    const { stringNullableRequired, numberNullableRequired } = useSchema();
    const [installmentOptions, setInstallmentOptions] = useState([]);
    const [showAlert, setShowAlert] = useState(false);
    const [currentAmount, setCurrentAmount] = useState(null);
    const [helpIsVisible, setHelpIsVisible] = useState(false);
    const [availableAccounts, setAvailableAccounts] = useState([{ id: "1" }]);
    const [showAmortizationInfo, setShowAmortizationInfo] = useState(false);
    const [selectedAccountId, setSelectedAccountId] = useState(null);

    const [selectedAccountLabel, setSelectedAccountLabel] = useState("");
    const duration = configUtils.get(`unsecured.loans.${loanType}.duration`);

    const preferentialRate = preferentialRates
        .find((rate) => duration >= rate.plazoDesde && duration <= rate.plazoHasta)
        ?.tasa?.toFixed(2);
        
    useEffect(() => {
        dispatch(accountsActions.listAccounts());
        dispatch(loansActions.unsecuresLoansApplicationPre("custom"));
    }, []);

    useEffect(() => {
        setShowAlert(
            qualificationAvailable === 0 ||
            qualificationLimit === 0 ||
            (currentAmount != null ? currentAmount > qualificationAvailable : false),
        );
    }, [qualificationAvailable, qualificationLimit, currentAmount]);

    useEffect(() => {
        if (selectedAccountId) {
            setSelectedAccountLabel(
                availableAccounts.filter((acc) => acc.id === selectedAccountId)[0].selectedAccountLabel,
            );
        }
    }, [selectedAccountId]);

    useEffect(() => {
        const formatOption = (account) => ({
            balance: { currency: account?.currency, quantity: account?.availableBalance },
            currency: account?.currency,
            frequentDestination: null,
            id: account?.productId,
            label: `${i18n.get(`productType.${account?.accountType}`)} ····${account?.accountNumber?.slice(
                -4,
            )} ${i18n.get(`currency.label.${account?.currency}`)}`,
            minimumPayment: 0,
            permissions: Object.keys(account?.permissions),
            statementsLoan: null,
            totalPayment: 0,
            type: account?.accountType,
            number: account?.accountNumber,
            selectedAccountLabel: `····${account?.accountNumber?.slice(-4)} ${account?.accountType} ${i18n.get(
                `currency.label.${account?.currency}`,
            )}`,
        });

        if (accounts?.length) {
            setAvailableAccounts(accounts.filter((acc) => acc.currency === "ARS")?.map((acc) => formatOption(acc)));
        }
    }, [accounts]);

    // Formik Validation Control
    const FORM_ID = "unsecuredLoanForm";

    const handleChangeDuration = (value) => {
        setInstallmentOptions(calculateInstallments(value));
    };

    function calculateInstallments(loanDurationMonths) {
        const periodicities = [
            { periodicity: "30", divisor: 1 },
            { periodicity: "60", divisor: 2 },
            { periodicity: "90", divisor: 3 },
        ];

        return periodicities
            .map(({ periodicity, divisor }) => ({
                value: periodicity,
                label: `${loanDurationMonths / divisor} Cuotas`,
                installments: loanDurationMonths / divisor,
            }))
            .filter(({ installments }) => Number.isInteger(installments));
    }

    function calculateNumberOfInstallments(duration, installments) {
        let divisor = 0;
        switch (installments) {
            case "30":
                divisor = 1;
                break;
            case "60":
                divisor = 2;
                break;
            case "90":
                divisor = 3;
                break;
            default:
                break;
        }
        return duration / divisor;
    }

    const validateAmount = (amount) => {
        setCurrentAmount(amount);
        return parseFloat(minAmount) <= amount && amount <= parseFloat(maxAmount);
    };

    const fieldDescription = (text) => (
        <p
            style={{
                textAlign: "right",
                marginTop: "-15px",
                color: "var(--color-blue)",
                fontSize: "var(--fontSize6)",
            }}>
            {text}
        </p>
    );

    const formikProps = {
        validateOnChange: true,
        validateOnBlur: false,
        initialValues: {
            amountField: "",
            duration: null,
            installments: null,
            rate,
            amortizationType: null,
            debitAccount: null,
            rateType: "",
            paymentDate: null,
        },
        validationSchema: () =>
            Yup.object().shape({
                amountField: Yup.object()
                    .shape({
                        amount: numberNullableRequired,
                    })
                    .test(
                        "amount",
                        i18n.get("transfer.amount.error"), // eslint-disable-next-line func-names
                        (value) => validateAmount(value.amount),
                    ),
                duration: stringNullableRequired,
                installments: stringNullableRequired,
                paymentDate: Yup.date().required("Required"),
                rateType: stringNullableRequired,
                amortizationType: stringNullableRequired,
                debitAccount: stringNullableRequired,
            }),
        onSubmit: (values) => {
            if (values) {
                const { amountField, debitAccount, installments, duration, amortizationType, paymentDate } = values;
                const { amount } = amountField;

                dispatch(
                    loansActions.unsecuredLoansApplicationPreview({
                        debitAccount,
                        totalOperationAmount: amount,
                        installments: calculateNumberOfInstallments(duration, installments),
                        periodicity: installments,
                        paymentDate,
                        amortizationType: amortizationType === "01" ? "FI" : amortizationType === "02" ? "AA" : "",
                        duration,
                        loanType,
                    }),
                );
            }
        },
    };

    const currencies = [{ id: "ARS", label: i18n.get(`currency.label.ARS`) }];

    const durationOptions = configUtils
        .getArray(`unsecured.loans.${loanType}.form.field.duration.options`)
        .map((duration) => ({
            label: `${duration} Meses`,
            value: duration,
        }));
    const rateTypeOptions = configUtils
        .getArray(`unsecured.loans.${loanType}.form.field.rateType.options`)
        .map((rate) => ({
            label: i18n.get(`unsecured.loans.rateType.${rate}`),
            value: rate,
        }));

    const amortizationTypes = configUtils
        .getArray(`unsecured.loans.common.form.amortizationTypes`)
        .map((amortizationType) => ({
            label: i18n.get(`unsecured.loans.common.form.amortizationTypes.${amortizationType}.text`),
            value: amortizationType,
        }));
    const renderHeader = () => {
        const renderMenuOption = () => (
            <>
                <Button
                    className="head__icon-button"
                    bsStyle="link"
                    image="images/figma/icon/info.svg"
                    block={false}
                    style={{ gridColumn: "10 / span 3", padding: 0, marginLeft: "auto" }}
                    label="loans.applyForLoan.header.moreInfo.text"
                    onClick={() => setHelpIsVisible(true)}
                />
                <MasInformacion slideIsVisible={helpIsVisible} closeSlide={() => setHelpIsVisible(false)} />
            </>
        );
        return (
            <CustomProductHead
                defaultTitle={`Solicitar tu ${i18n.get(`unsecured.loans.${loanType}.title`)}`}
                backLinkTo="/applyForLoan"
                addLinkToLabel="deposits.new"
                addLinkTo="/applyForLoan"
                className="deposits"
                renderMenuOption={renderMenuOption}>
                <div className="toolbar-item view-title">
                    <ViewTitle defaultValue={`Solicitar tu ${i18n.get(`unsecured.loans.${loanType}.title`)}`} />
                </div>
            </CustomProductHead>
        );
    };

    const AmortizationInfo = () => (
        <Drawer
            className="help-slider"
            openSidebar={showAmortizationInfo}
            toggleSidebar={() => setShowAmortizationInfo(false)}>
            <div className="container">
                <div
                    className="row"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: i18n.get("loans.common.amortizationInformation.content"),
                    }}
                />
            </div>
        </Drawer>
    );

    return (
        <Fragment>
            <Notification scopeToShow="loans" />
            {renderHeader()}
            <AmortizationInfo />
            <MainContainer showLoader={fetching || !qualificationAvailable} className="loans">
                <Formik {...formikProps} ref={formRef}>
                    {(formik) => (
                        <Form id={FORM_ID}>
                            {showAlert && (
                                <div className="container--alert" role="alert">
                                    <div className="alert alert-warning">
                                        <CustomData
                                            icon="alert.svg"
                                            titleText="Actualmente, tiene saldo insuficiente para operar. Completa la solicitud y un asesor lo evaluará."
                                            keepSpaceIfNoValue={false}
                                        />
                                    </div>
                                </div>
                            )}
                            <Row className="form-background">
                                <Col xs={12} md={5} mdOffset={1}>
                                    <Field
                                        component={AmountField}
                                        labelText={`${i18n.get("", "Monto")}`}
                                        name="amountField"
                                        disable={!minAmount === null || !maxAmount === null || loanSimulation}
                                        data={{ options: currencies }}
                                        clearable={false}
                                        maxLength={16}
                                        disableSelect
                                        fixedDecimalScale
                                    />
                                    {minAmount !== null &&
                                        maxAmount !== null &&
                                        fieldDescription(
                                            `${i18n.get("", "Mínimo")} $${numberUtils.formatAmountStrDouble(
                                                minAmount,
                                                undefined,
                                                ".",
                                            )} ${i18n.get("", "máximo")} $${numberUtils.formatAmountStrDouble(
                                                maxAmount,
                                                undefined,
                                                ".",
                                            )}`,
                                        )}
                                </Col>
                                <Col xs={12} md={5} mdOffset={6}>
                                    <Field
                                        component={Selector}
                                        idForm={FORM_ID}
                                        name="duration"
                                        label={`${i18n.get("unsecured.loans.common.form.fields.duration.label")}`}
                                        placeholder={i18n.get("", "Seleccioná una opción")}
                                        disabled={!durationOptions?.length || loanSimulation}
                                        options={durationOptions}
                                        onChange={handleChangeDuration}
                                    />
                                    {fieldDescription(
                                        i18n.get("unsecured.loans.common.form.fields.duration.description"),
                                    )}
                                </Col>
                                <Col xs={12} md={5} mdOffset={1}>
                                    <Field
                                        component={Selector}
                                        idForm={FORM_ID}
                                        name="installments"
                                        label={`${i18n.get("unsecured.loans.common.form.fields.installments.label")}`}
                                        placeholder={i18n.get("", "Seleccioná una opción")}
                                        disabled={!formik?.values?.duration?.length || loanSimulation}
                                        options={installmentOptions}
                                    />
                                    {fieldDescription(
                                        i18n.get("unsecured.loans.common.form.fields.installments.description"),
                                    )}
                                </Col>
                                <Col xs={12} md={5} mdOffset={6}>
                                    <Field
                                        idForm={FORM_ID}
                                        name="paymentDate"
                                        placeholder="Seleccioná una fecha"
                                        labelText={i18n.get("unsecured.loans.common.form.fields.paymentDate.label")}
                                        component={DateField}
                                        startDate={new Date()}
                                        minDate={new Date()}
                                        maxDate={null}
                                        disabled={loanSimulation}
                                    />
                                    {fieldDescription(
                                        i18n.get("unsecured.loans.common.form.fields.paymentDate.description"),
                                    )}
                                </Col>
                                <Col xs={12} md={5} mdOffset={1}>
                                    <Field
                                        idForm={FORM_ID}
                                        name="rateType"
                                        placeholder={i18n.get("", "Seleccioná una opción")}
                                        label={i18n.get("unsecured.loans.common.form.fields.rateType.label")}
                                        component={Selector}
                                        options={rateTypeOptions}
                                        disabled={loanSimulation}
                                    />
                                    {fieldDescription(
                                        i18n.get("unsecured.loans.common.form.fields.rateType.description"),
                                    )}
                                </Col>
                                <Col xs={12} md={5} mdOffset={6}>
                                    <div>
                                        <Field
                                            autoComplete="off"
                                            component={Selector}
                                            idForm={FORM_ID}
                                            name="rate"
                                            placeholder={
                                                <>
                                                    {preferentialRate && <s style={{ marginRight: "30px" }}>{rate}%</s>}
                                                    {preferentialRate ? `${preferentialRate}%` : `${rate}%`}
                                                </>
                                            }
                                            labelTextDefault={i18n.get("unsecured.loans.common.form.fields.rate.label")}
                                            label={i18n.get("unsecured.loans.common.form.fields.rate.label")}
                                            spaceEnabled={false}
                                            disabled
                                        />
                                    </div>
                                </Col>
                                <Col xs={12} md={5} mdOffset={1}>
                                    <Field
                                        idForm={FORM_ID}
                                        name="amortizationType"
                                        label={`${i18n.get(
                                            "unsecured.loans.common.form.fields.amortizationType.label",
                                        )}`}
                                        placeholder={i18n.get("", "Seleccioná una opción")}
                                        component={Selector}
                                        options={amortizationTypes}
                                        showTooltip
                                        onTooltipClick={() => setShowAmortizationInfo(true)}
                                        disabled={loanSimulation}
                                    />
                                </Col>
                                <Col xs={12} md={12} mdOffset={1}>
                                    <div
                                        className="grid-table-row md:col-start-2 md:col-span-10"
                                        style={{ width: isDesktop ? "90%" : "100%" }}>
                                        <Field
                                            idForm={FORM_ID}
                                            name="debitAccount"
                                            component={ProductSelector}
                                            label={`${i18n.get("unsecured.loans.common.form.fields.account.label")}`}
                                            placeholder={i18n.get("deposits.request.filters.debitAccount.placeHolder")}
                                            data={{
                                                emptyOptionLabel: "",
                                                options: availableAccounts,
                                            }}
                                            onChange={setSelectedAccountId}
                                            disabled={!accounts.length || loanSimulation}
                                        />
                                        {fieldDescription(
                                            i18n.get("unsecured.loans.common.form.fields.account.description"),
                                        )}
                                    </div>
                                </Col>
                            </Row>
                            <GridTable className="form-actions grid-table-buttons mb-5">
                                <GridTable.Container columns={12} columnsTemplate="fixed">
                                    <GridTable.Data columnStart={5} columnWidth={4}>
                                        <CustomButton
                                            text={i18n.get("global.simulate")}
                                            type="submit"
                                            disabled={!formik.isValid}
                                            loading={fetchingSimulation}
                                            className="btn-primary flex"
                                        />
                                    </GridTable.Data>
                                </GridTable.Container>
                            </GridTable>

                            {loanSimulation ? (
                                <UnsecuredLoanFormConfirm loanSimulation={loanSimulation} isDesktop />
                            ) : null}
                        </Form>
                    )}
                </Formik>
            </MainContainer>
        </Fragment>
    );
};

UnsecuredLoanForm.propTypes = {
    fetching: bool.isRequired,
    fetchingSimulation: bool.isRequired,
    qualificationAvailable: number.isRequired,
    qualificationLimit: number.isRequired,
    minAmount: number.isRequired,
    maxAmount: number.isRequired,
    accounts: arrayOf(shape({})).isRequired,
    isDesktop: oneOfType([string, bool]).isRequired,
    loanType: arrayOf(string).isRequired,
    rate: string.isRequired,
    loanSimulation: shape({}).isRequired,
};

const mapStateToProps = (state) => ({
    fetching: loansSelectors.getFetching(state),
    fetchingSimulation: loansSelectors.getFetchingSimulation(state),
    detailSubproductLoan: loansSelectors.getDetailSubproductLoan(state),
    qualificationAvailable: loansSelectors.getQualificationAvailable(state),
    qualificationLimit: loansSelectors.getQualificationLimit(state),
    minAmount: loansSelectors.getMinAmount(state),
    maxAmount: loansSelectors.getMaxAmount(state),
    fetchingAccounts: accountsSelectors.getFetching(state),
    accounts: accountsSelectors.getAccounts(state),
    rate: state.loans.rate,
    loanType: state.loans.loanType,
    preferentialRates: loansSelectors.getPreferentialRates(state),
    loanSimulation: loansSelectors.getLoanSimulation(state),
});

export default connect(mapStateToProps)(UnsecuredLoanForm);
